import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { APIProvider, Map, Marker, InfoWindow } from "@vis.gl/react-google-maps";
import { userService } from "../../../apis/users";
import { User, UserResponse } from "../../../types/users";
import { partnerDashboardService } from "../../../apis/partnerDashboard";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { jwtDecode } from "jwt-decode";
import normalCar from "../../../assets/images/normal_car.png";
import normalVan from "../../../assets/images/normal_van.png";
import normalBike from "../../../assets/images/normal_bike.png";
import normalTruck from "../../../assets/images/normal_truck.png";
import normalSharedCar from "../../../assets/images/normal_shared_car.png";
import teracarCar from "../../../assets/images/teracar_car.png";
import teracarVan from "../../../assets/images/teracar_van.png";
import teracarBike from "../../../assets/images/teracar_bike.png";
import {
  formatTextToCapitalise,
  getDriverTypes,
  textCapitalise,
} from "../../../utils/util";

interface PartnerMapProps {
  partnerId: string;
}

interface Location {
  lat: number;
  lng: number;
}

interface DeliveryCapacity {
  max_weight: number;
  max_width: number;
  max_height: number;
  max_length: number;
}

interface Client {
  clientId: string;
  clientRole: string;
  ownerName?: string;
  carType?: string;
  email?: string;
  phoneNumber?: string;
  acceptSharedRides?: boolean;
  acceptDeliveries?: boolean;
  isPremiumVtc?: boolean;
  isCommunityVtc?: boolean;
  driverType?: string;
  deliveryCapacity?: DeliveryCapacity;
  isAvailable?: boolean;
  isOngoingRide?: boolean;
  location?: {
    latitude: number;
    longitude: number;
  };
  connectedTime?: string;
  partner_id?: string;
  is_internal_driver?: string | boolean;
}

interface Event {
  event: string;
  client: Client;
  error: string;
  connections: string;
}

interface DriverLocation {
  driverId: string;
  location: Location;
  color: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface MapViewProps {
  drivers: User[];
  connectedClients: Client[];
  selectedRegion?: any;
  partnerId?: string;
  partnerDriverIds?: Set<string>;
}

interface CustomMarkerProps {
  position: Location;
  label: string;
  color: string;
  onClick: () => void;
  driver: User;
  isOnline?: boolean;
}

// Add styles for the custom marker label
const styles = `
  .custom-marker-label {
    background-color: rgba(255, 255, 255, 0.9);
    padding: 4px 8px;
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    white-space: pre-line;
    min-width: 100px;
    text-align: center;
    transform: translateY(-30px);
    font-family: Roboto, Arial, sans-serif;
    line-height: 1.4;
    color: #333333;
  }
`;

const CustomMarker: React.FC<CustomMarkerProps & { isInternalDriver?: boolean }> = ({ position, label, onClick, driver, isInternalDriver }) => {
  const getMarkerIcon = () => {
    const isShared = driver.driverType === "professional" || driver.accept_shared_rides;

    if (!driver.carType) {
      return isShared ? normalSharedCar : driver.is_teracar_driver ? teracarCar : normalCar;
    }

    switch (driver.carType) {
      case "VAN":
        return driver.is_teracar_driver ? teracarVan : normalVan;
      case "MOTOR_BIKE":
        return driver.is_teracar_driver ? teracarBike : normalBike;
      case "MEDIUM_TRUCK":
        return driver.is_teracar_driver ? normalTruck : normalTruck;
      case "LARGE_TRUCK":
        return driver.is_teracar_driver ? normalTruck : normalTruck;
      case "TRIKE":
        return driver.is_teracar_driver ? normalTruck : normalTruck;
      case "GREEN":
      case "CLASSIC":
      default:
        return driver.is_teracar_driver ? teracarCar : isShared ? normalSharedCar : normalCar;
    }
  };

  // Format lastLocationUpdated for display with time
  const formatLastLocationTime = () => {
    if (!driver.lastLocationUpdated) return "";
    try {
      const date = new Date(driver.lastLocationUpdated);
      if (isNaN(date.getTime())) {
        return "";
      }
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'Min' : 'Mins'} ago`;
      if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'Hour' : 'Hours'} ago`;
      if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'Day' : 'Days'} ago`;
      return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return "";
    }
  };

  const lastLocationTime = formatLastLocationTime();
  const driverTypeLabel = isInternalDriver ? "Internal" : "External";
  
  // Combine driver name, type, and Last Online time on separate lines
  const labelText = lastLocationTime 
    ? `${label} (${driverTypeLabel})\nLast Online: ${lastLocationTime}`
    : `${label} (${driverTypeLabel})`;

  return (
    <Marker
      position={position}
      onClick={onClick}
      label={{
        text: labelText,
        color: "#333333",
        fontSize: "12px",
        fontWeight: "500",
        className: "custom-marker-label",
      }}
      icon={{
        url: getMarkerIcon(),
        scaledSize: new google.maps.Size(32, 32),
        anchor: new google.maps.Point(16, 16),
      }}
    />
  );
};

const MapView: React.FC<MapViewProps> = ({ drivers, connectedClients, selectedRegion, partnerId, partnerDriverIds }) => {
  // Filter connectedClients to only those that belong to this partner
  const filteredConnectedClients = useMemo(() => {
    if (!partnerId) return connectedClients;
    return connectedClients.filter((client) => {
      if (client.partner_id === partnerId) return true;
      if (!client.partner_id && partnerDriverIds && partnerDriverIds.has(client.clientId)) return true;
      return false;
    });
  }, [connectedClients, partnerId, partnerDriverIds]);
  const [selectedDriver, setSelectedDriver] = useState<User | null>(null);
  const [driverLocations, setDriverLocations] = useState<DriverLocation[]>([]);
  const [lastUpdateTime, setLastUpdateTime] = useState<number>(0);
  const [lastProcessedClients, setLastProcessedClients] = useState<Set<string>>(new Set());

  // Get color based on driver type
  const getDriverColor = (driverType?: string | null) => {
    switch (driverType) {
      case "delivery":
        return "#FF0000";
      case "professional":
        return "#0000FF";
      default:
        return "#00FF00";
    }
  };

  // Validate if a location object has valid coordinates
  const isValidLocation = (location: any): location is { latitude: number; longitude: number } => {
    if (!location) return false;
    let parsedLocation = location;
    if (typeof location === "string") {
      try {
        parsedLocation = JSON.parse(location);
      } catch (e) {
        return false;
      }
    }
    if (typeof parsedLocation !== "object") return false;
    if (typeof parsedLocation.latitude !== "number" || typeof parsedLocation.longitude !== "number") return false;
    if (isNaN(parsedLocation.latitude) || isNaN(parsedLocation.longitude)) return false;
    if (parsedLocation.latitude < -90 || parsedLocation.latitude > 90) return false;
    if (parsedLocation.longitude < -180 || parsedLocation.longitude > 180) return false;
    return true;
  };

  // Remove duplicates and filter only drivers with valid locations
  const processClients = useCallback((clients: Client[]) => {
    const uniqueClientsMap = clients.reduce((acc: { [key: string]: Client }, client) => {
      if (client.clientRole === "DRIVER" && client.location) {
        acc[client.clientId] = client;
      }
      return acc;
    }, {});
    return Object.values(uniqueClientsMap);
  }, []);

  // Check if locations have actually changed
  const hasLocationChanges = useCallback(
    (newLocations: DriverLocation[], oldLocations: DriverLocation[]) => {
      if (newLocations.length !== oldLocations.length) return true;
      return newLocations.some((newLoc) => {
        const oldLoc = oldLocations.find((loc) => loc.driverId === newLoc.driverId);
        if (!oldLoc) return true;
        return oldLoc.location.lat !== newLoc.location.lat || oldLoc.location.lng !== newLoc.location.lng;
      });
    },
    []
  );

  // Debounced update function
  const debouncedUpdateLocations = useCallback(
    (clients: Client[]) => {
      const now = Date.now();
      if (now - lastUpdateTime < DEBOUNCE_DELAY) return;

      const uniqueClients = processClients(clients);
      const currentClientIds = new Set<string>(uniqueClients.map((client) => client.clientId));
      const clientsChanged =
        currentClientIds.size !== lastProcessedClients.size ||
        !Array.from(currentClientIds).every((id) => lastProcessedClients.has(id));

      if (!clientsChanged) return;

      setLastUpdateTime(now);
      setLastProcessedClients(currentClientIds);

      const updatedLocations = (uniqueClients as Client[])
        .filter(
          (client): client is Client & { location: { latitude: number; longitude: number } } => {
            let location = client.location;
            if (typeof location === "string") {
              try {
                location = JSON.parse(location);
              } catch (e) {
                return false;
              }
            }
            if (!location || !isValidLocation(location)) return false;
            return true;
          }
        )
        .map((client) => {
          const driver = drivers.find((d) => d._id === client.clientId);
          if (!driver) return null;

          let location = client.location;
          if (typeof location === "string") {
            location = JSON.parse(location);
          }

          return {
            driverId: client.clientId,
            location: {
              lat: Number(location.latitude),
              lng: Number(location.longitude),
            },
            color: getDriverColor(client.driverType),
          };
        })
        .filter((loc): loc is NonNullable<typeof loc> => loc !== null);

      if (hasLocationChanges(updatedLocations, driverLocations)) {
        setDriverLocations(updatedLocations);
      }
    },
    [lastUpdateTime, drivers, driverLocations, lastProcessedClients, processClients, hasLocationChanges]
  );

  // Update driver locations when connected clients change
  useEffect(() => {
    debouncedUpdateLocations(filteredConnectedClients);
  }, [filteredConnectedClients, debouncedUpdateLocations]);

  // Process offline drivers with location data
  useEffect(() => {
    if (!drivers || drivers.length === 0) return;

    const onlineDriverIds = new Set(filteredConnectedClients.map((client) => client.clientId));
    
    const offlineDriversWithLocations = drivers.filter((driver) => {
      if (onlineDriverIds.has(driver._id)) return false;
      return driver.location && 
             typeof driver.location.latitude === 'number' && 
             typeof driver.location.longitude === 'number' &&
             !isNaN(driver.location.latitude) &&
             !isNaN(driver.location.longitude);
    });

    const offlineLocations: DriverLocation[] = offlineDriversWithLocations.map((driver) => ({
      driverId: driver._id,
      location: {
        lat: Number(driver.location!.latitude),
        lng: Number(driver.location!.longitude),
      },
      color: getDriverColor(driver.driverType),
    }));

    setDriverLocations((prevLocations) => {
      const locationMapObj: { [key: string]: DriverLocation } = {};
      prevLocations.forEach((loc) => {
        locationMapObj[loc.driverId] = loc;
      });
      offlineLocations.forEach((loc) => {
        if (!locationMapObj[loc.driverId]) {
          locationMapObj[loc.driverId] = loc;
        }
      });
      const mergedLocations = Object.values(locationMapObj);
      if (hasLocationChanges(mergedLocations, prevLocations)) {
        return mergedLocations;
      }
      return prevLocations;
    });
  }, [drivers, filteredConnectedClients, hasLocationChanges]);

  // Dynamic map center based on selected region
  const mapCenter = useMemo(() => {
    if (selectedRegion) {
      return {
        lat: selectedRegion.lat,
        lng: selectedRegion.lng,
      };
    }
    return {
      lat: 5.3599, // Abidjan latitude
      lng: -4.0083, // Abidjan longitude
    };
  }, [selectedRegion]);

  return (
    <Box sx={{ height: "600px", width: "100%", position: "relative" }}>
      <style>{styles}</style>
      <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY!}>
        <Map
          defaultCenter={mapCenter}
          defaultZoom={12}
          gestureHandling={"greedy"}
          disableDefaultUI={false}
        >
          <>
            {driverLocations.length === 0 ? (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  padding: "20px",
                  borderRadius: "4px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                  zIndex: 1000,
                }}
              >
                <Typography>No drivers currently available on the map</Typography>
              </Box>
            ) : (
              <>
                {driverLocations.map((driverLoc) => {
                  const driver = drivers.find((d) => d._id === driverLoc.driverId);
                  if (!driver) return null;

                  // Find client from filteredConnectedClients (already filtered by partner)
                  const client = filteredConnectedClients.find((c) => c.clientId === driver._id);
                  const isOnline = !!client;
                  const isInternal = client && client.is_internal_driver !== undefined
                    ? (typeof client.is_internal_driver === 'string' 
                        ? client.is_internal_driver === "true"
                        : client.is_internal_driver === true)
                    : false;

                  return (
                    <CustomMarker
                      key={driverLoc.driverId}
                      position={driverLoc.location}
                      label={`${driver.firstName} ${driver.lastName}`}
                      color={driverLoc.color}
                      onClick={() => setSelectedDriver(driver)}
                      driver={driver}
                      isOnline={isOnline}
                      isInternalDriver={isInternal}
                    />
                  );
                })}
              </>
            )}

            {selectedDriver && (
              <InfoWindow
                position={
                  driverLocations.find((dl) => dl.driverId === selectedDriver._id)?.location ||
                  mapCenter
                }
                onCloseClick={() => setSelectedDriver(null)}
              >
                <Box
                  sx={{
                    p: 2,
                    minWidth: "250px",
                    "& .MuiTypography-root": {
                      fontFamily: "Roboto, Arial, sans-serif",
                    },
                  }}
                >
                  <Box
                    sx={{
                      borderBottom: "2px solid #f0f0f0",
                      pb: 1.5,
                      mb: 1.5,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "#1a1a1a",
                        mb: 0.5,
                      }}
                    >
                      {`${selectedDriver.firstName} ${selectedDriver.lastName}`}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, mb: 0.5 }}>
                      {(() => {
                        // Find client from filteredConnectedClients (already filtered by partner)
                        const client = filteredConnectedClients.find((c) => c.clientId === selectedDriver._id);
                        const isInternal = client && client.is_internal_driver !== undefined
                          ? (typeof client.is_internal_driver === 'string' 
                              ? client.is_internal_driver === "true"
                              : client.is_internal_driver === true)
                          : false;
                        const isOnline = !!client;
                        return (
                          <>
                            <Chip
                              label={isInternal ? "Internal" : "External"}
                              color={isInternal ? "primary" : "info"}
                              size="small"
                            />
                            <Chip
                              label={isOnline ? "Online" : "Offline"}
                              color={isOnline ? "success" : "error"}
                              size="small"
                            />
                          </>
                        );
                      })()}
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      {selectedDriver.driverType === "professional"
                        ? "Shared Driver"
                        : selectedDriver.driverType
                        ? `${textCapitalise(selectedDriver.driverType)} Driver`
                        : "Normal Driver"}
                    </Typography>
                    {selectedDriver.lastLocationUpdated && (() => {
                      try {
                        const date = new Date(selectedDriver.lastLocationUpdated);
                        if (isNaN(date.getTime())) {
                          return null;
                        }
                        const now = new Date();
                        const diffMs = now.getTime() - date.getTime();
                        const diffMins = Math.floor(diffMs / 60000);
                        const diffHours = Math.floor(diffMins / 60);
                        const diffDays = Math.floor(diffHours / 24);

                        let timeAgo = "";
                        if (diffMins < 1) timeAgo = "Just now";
                        else if (diffMins < 60) timeAgo = `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
                        else if (diffHours < 24) timeAgo = `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
                        else if (diffDays < 7) timeAgo = `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
                        else timeAgo = date.toLocaleString();

                        return (
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#999",
                              fontSize: "0.75rem",
                              fontStyle: "italic",
                              mt: 0.5,
                            }}
                          >
                            Last Location: {timeAgo}
                          </Typography>
                        );
                      } catch (e) {
                        return null;
                      }
                    })()}
                  </Box>

                  <Box sx={{ mb: 1.5 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: "#666",
                        mb: 0.5,
                        fontWeight: 500,
                      }}
                    >
                      Contact Information
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                        pl: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          color: "#333",
                        }}
                      >
                        <span style={{ color: "#666", minWidth: "80px" }}>Phone:</span>
                        {selectedDriver.phone || "N/A"}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          color: "#333",
                        }}
                      >
                        <span style={{ color: "#666", minWidth: "80px" }}>Email:</span>
                        {selectedDriver.email || "N/A"}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 1.5 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: "#666",
                        mb: 0.5,
                        fontWeight: 500,
                      }}
                    >
                      Vehicle Information
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                        pl: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          color: "#333",
                        }}
                      >
                        <span style={{ color: "#666", minWidth: "80px" }}>Car Type:</span>
                        {selectedDriver.carType || "N/A"}
                      </Typography>
                    </Box>
                  </Box>

                  {(selectedDriver.totalRideCount !== undefined || selectedDriver.rejectedRideCount !== undefined) && (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: "#666",
                          mb: 0.5,
                          fontWeight: 500,
                        }}
                      >
                        Ride Statistics
                      </Typography>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 1,
                          pl: 1,
                        }}
                      >
                        <Box
                          sx={{
                            p: 1,
                            backgroundColor: "#f5f5f5",
                            borderRadius: "4px",
                            textAlign: "center",
                          }}
                        >
                          <Typography variant="body2" sx={{ color: "#666", mb: 0.5 }}>
                            Total Rides
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{
                              color: "#1a1a1a",
                              fontWeight: 600,
                            }}
                          >
                            {selectedDriver.totalRideCount || 0}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            p: 1,
                            backgroundColor: "#f5f5f5",
                            borderRadius: "4px",
                            textAlign: "center",
                          }}
                        >
                          <Typography variant="body2" sx={{ color: "#666", mb: 0.5 }}>
                            Rejected Rides
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{
                              color:
                                selectedDriver.rejectedRideCount &&
                                selectedDriver.rejectedRideCount > 0
                                  ? "#d32f2f"
                                  : "#1a1a1a",
                              fontWeight: 600,
                            }}
                          >
                            {selectedDriver.rejectedRideCount || 0}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              </InfoWindow>
            )}
          </>
        </Map>
      </APIProvider>
    </Box>
  );
};

const DEBOUNCE_DELAY = 60000; // 1 minute delay for location updates
const WEBSOCKET_REFRESH_INTERVAL = 300000; // 5 minutes

const PartnerMap: React.FC<PartnerMapProps> = ({ partnerId }) => {
  const [tabValue, setTabValue] = useState(0);
  const [allDrivers, setAllDrivers] = useState<User[]>([]);
  const [connectedClients, setConnectedClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState<number>(Date.now());
  const [search, setSearch] = useState("");
  const [driverType, setDriverType] = useState<string | "">("all");
  const [carType, setCarType] = useState<string | "">("all");
  const [minRejectedRides, setMinRejectedRides] = useState<number | "">("");
  const [maxRejectedRides, setMaxRejectedRides] = useState<number | "">("");
  const [minRides, setMinRides] = useState<number | "">("");
  const [maxRides, setMaxRides] = useState<number | "">("");
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [viewMode, setViewMode] = useState<"table" | "map">("map");
  const [selectedRegion, setSelectedRegion] = useState<any>(null);

  const [partnerDriverIds, setPartnerDriverIds] = useState<Set<string>>(new Set());

  // Fetch partner drivers list to get driver IDs
  useEffect(() => {
    const fetchPartnerDriverIds = async () => {
      try {
        const partnerDrivers = await partnerDashboardService.getPartnerDrivers(partnerId);
        const driverIdSet = new Set(partnerDrivers.map((driver) => driver._id));
        setPartnerDriverIds(driverIdSet);
        console.log("Partner driver IDs:", Array.from(driverIdSet));
      } catch (error) {
        console.error("Error fetching partner drivers:", error);
      }
    };
    fetchPartnerDriverIds();
  }, [partnerId]);

  // Filter all drivers to only include partner's drivers
  const partnerDrivers = useMemo(() => {
    return allDrivers.filter((driver) => partnerDriverIds.has(driver._id));
  }, [allDrivers, partnerDriverIds]);

  const setConnectedClientsWithTime = useCallback((connections: Client[]) => {
    console.log("setConnectedClientsWithTime called with connections:", connections.length);
    
    // Store ALL connections like WebSocketUI does (don't filter here)
    // We'll filter when determining online/offline status
    const uniqueClients: { [key: string]: Client } = {};

    // Keep only the most recent client data for each clientId
    connections?.forEach((client) => {
      // Only process DRIVER role clients
      if (client.clientRole !== "DRIVER") return;
      
      const existingClient = uniqueClients[client.clientId];
      if (
        !existingClient ||
        (client.connectedTime &&
          (!existingClient.connectedTime ||
            new Date(client.connectedTime) > new Date(existingClient.connectedTime)))
      ) {
        uniqueClients[client.clientId] = {
          ...client,
          connectedTime: client.connectedTime || new Date().toLocaleTimeString(),
        };
      }
    });

    const finalClients = Object.values(uniqueClients);
    console.log("Final connectedClients to set:", finalClients.length);
    console.log("Connected client IDs:", finalClients.map(c => c.clientId));
    console.log("Sample client:", finalClients[0]);
    
    setConnectedClients(finalClients);
    setLastUpdateTime(Date.now());
  }, []);

  const loadAllDrivers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const decoded: any = jwtDecode(token);
        let countryCode = "";
        if (decoded.countryCode && decoded.countryCode !== "all") {
          countryCode =
            decoded.countryCode === "uk"
              ? "GB"
              : decoded.countryCode === "france"
              ? "FR"
              : decoded.countryCode === "ivory-coast"
              ? "CI"
              : "";
        }

        // Use the same endpoint as WebSocketUI
        const data: UserResponse = await userService.getDriversWithLocations({
          page: 1,
          limit: 10000,
          status: "approved",
          country: countryCode,
        });
        setAllDrivers(data.users);
      }
    } catch (error) {
      console.error("Error fetching drivers:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAllDrivers();
  }, []);

  useEffect(() => {
    if (lastUpdateTime > 0) {
      loadAllDrivers();
    }
  }, [lastUpdateTime]);

  useEffect(() => {
    const API_KEY = process.env.REACT_APP_API_KEY;
    const socketBaseUrl = process.env.REACT_APP_WEB_SOCKET_SERVICE_BASE_URL;
    
    if (!API_KEY || !socketBaseUrl) {
      console.error("WebSocket API key or URL not configured");
      return;
    }

    let socket: WebSocket | null = null;
    let reconnectTimeout: NodeJS.Timeout;
    let reconnectAttempts = 0;
    const MAX_RECONNECT_ATTEMPTS = 5;
    const RECONNECT_DELAY = 5000;

    const connectWebSocket = () => {
      if (socket?.readyState === WebSocket.OPEN) {
        console.log("WebSocket already connected");
        return;
      }

      socket = new WebSocket(`${socketBaseUrl}?api_key=${API_KEY}`);

      socket.onopen = () => {
        console.log("WebSocket connection opened");
        reconnectAttempts = 0;
      };

      let messageQueue: Event[] = [];
      let processingQueue = false;
      let lastProcessTime = 0;
      const PROCESS_INTERVAL = 120000; // 2 minutes

      const processMessageQueue = () => {
        if (processingQueue || messageQueue.length === 0) return;

        const now = Date.now();
        const timeSinceLastProcess = now - lastProcessTime;

        if (timeSinceLastProcess < PROCESS_INTERVAL) {
          setTimeout(processMessageQueue, PROCESS_INTERVAL - timeSinceLastProcess);
          return;
        }

        processingQueue = true;
        const data = messageQueue.shift();

        if (data) {
          console.log("Processing queued message:", data.event);
          switch (data.event) {
            case "connect":
              setConnectedClientsWithTime(JSON.parse(data.connections));
              break;
            case "disconnect":
              setConnectedClientsWithTime(JSON.parse(data.connections));
              break;
            case "error":
              setConnectedClientsWithTime(JSON.parse(data.connections));
              break;
          }
          lastProcessTime = now;
        }

        processingQueue = false;
        if (messageQueue.length > 0) {
          setTimeout(processMessageQueue, PROCESS_INTERVAL);
        }
      };

      socket.onmessage = (event) => {
        const data = JSON.parse(event.data) as Event;
        messageQueue.push(data);
        if (!processingQueue) {
          processMessageQueue();
        }
      };

      socket.onerror = (error) => {
        console.error("WebSocket error:", error);
      };

      socket.onclose = (event) => {
        console.log("WebSocket connection closed", event.code, event.reason);
        if (event.code !== 1000 && reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttempts++;
          reconnectTimeout = setTimeout(connectWebSocket, RECONNECT_DELAY);
        }
      };
    };

    connectWebSocket();

    return () => {
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      if (socket) {
        socket.close(1000, "Component unmounting");
      }
    };
  }, [partnerId, setConnectedClientsWithTime, partnerDriverIds]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getOnlineDrivers = () => {
    // Filter connectedClients to only those that belong to this partner
    // Check partner_id if available, otherwise verify clientId is in partnerDriverIds
    const partnerOnlineClients = connectedClients.filter((client) => {
      // If partner_id is set and matches, include it
      if (client.partner_id === partnerId) return true;
      // If partner_id is not set, check if clientId is in partnerDriverIds
      if (!client.partner_id && partnerDriverIds.has(client.clientId)) return true;
      return false;
    });
    
    const onlineDriverIds = new Set(partnerOnlineClients.map((client) => client.clientId));
    console.log("getOnlineDrivers - partnerOnlineClients:", partnerOnlineClients.length);
    console.log("getOnlineDrivers - onlineDriverIds:", Array.from(onlineDriverIds));
    console.log("getOnlineDrivers - partnerDrivers count:", partnerDrivers.length);
    const onlineDrivers = partnerDrivers.filter((driver) => onlineDriverIds.has(driver._id));
    console.log("getOnlineDrivers - result count:", onlineDrivers.length);
    return onlineDrivers;
  };

  const getOfflineDrivers = () => {
    // Filter connectedClients to only those that belong to this partner
    const partnerOnlineClients = connectedClients.filter((client) => {
      if (client.partner_id === partnerId) return true;
      if (!client.partner_id && partnerDriverIds.has(client.clientId)) return true;
      return false;
    });
    
    const onlineDriverIds = new Set(partnerOnlineClients.map((client) => client.clientId));
    console.log("getOfflineDrivers - partnerOnlineClients:", partnerOnlineClients.length);
    console.log("getOfflineDrivers - onlineDriverIds:", Array.from(onlineDriverIds));
    console.log("getOfflineDrivers - partnerDrivers count:", partnerDrivers.length);
    const offlineDrivers = partnerDrivers.filter((driver) => !onlineDriverIds.has(driver._id));
    console.log("getOfflineDrivers - result count:", offlineDrivers.length);
    return offlineDrivers;
  };

  // Helper to get internal/external status from Client data
  const getDriverInternalStatus = (driverId: string): boolean => {
    // Find client that belongs to this partner
    const client = connectedClients.find((c) => {
      if (c.clientId !== driverId) return false;
      // Verify it belongs to this partner
      if (c.partner_id === partnerId) return true;
      if (!c.partner_id && partnerDriverIds.has(c.clientId)) return true;
      return false;
    });
    
    if (client && client.is_internal_driver !== undefined) {
      return typeof client.is_internal_driver === 'string' 
        ? client.is_internal_driver === "true"
        : client.is_internal_driver === true;
    }
    return false; // Default to external if not found
  };

  const handleRefresh = () => {
    loadAllDrivers();
  };

  const downloadPDF = (drivers: User[], title: string) => {
    const doc = new jsPDF();
    doc.text(title, 14, 15);

    // Filter connectedClients to only those that belong to this partner
    const partnerOnlineClients = connectedClients.filter((client) => {
      if (client.partner_id === partnerId) return true;
      if (!client.partner_id && partnerDriverIds.has(client.clientId)) return true;
      return false;
    });
    const onlineDriverIds = new Set(partnerOnlineClients.map((client) => client.clientId));
    const tableColumn = ["Name", "Email", "Phone", "Car Type", "Driver Type", "Internal/External", "Status"];
    const tableRows = drivers.map((driver) => {
      const isOnline = onlineDriverIds.has(driver._id);
      const isInternal = getDriverInternalStatus(driver._id);
      return [
        `${driver.firstName} ${driver.lastName}`,
        driver.email || "-",
        driver.phone || "-",
        driver.carType || "-",
        driver.driverType ? getDriverTypes(driver) : "-",
        isInternal ? "Internal" : "External",
        isOnline ? "Online" : "Offline",
      ];
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 25,
      theme: "grid",
      styles: { fontSize: 8 },
      headStyles: { fillColor: [41, 128, 185] },
    });

    doc.save(
      `${title.toLowerCase().replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.pdf`
    );
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleFilterChange =
    (setter: React.Dispatch<React.SetStateAction<any>>) => (value: any) => {
      setter(value);
    };

  const filterDrivers = (drivers: User[]) => {
    return drivers.filter((driver) => {
      const searchLower = search.toLowerCase();
      const matchesSearch =
        search === "" ||
        `${driver.firstName} ${driver.lastName}`.toLowerCase().includes(searchLower) ||
        (driver.email || "").toLowerCase().includes(searchLower) ||
        (driver.phone || "").toLowerCase().includes(searchLower);

      const matchesDriverType = driverType === "all" || driver.driverType === driverType;
      const matchesCarType = carType === "all" || driver.carType === carType;

      const rejectedRides = driver.rejectedRideCount || 0;
      const matchesRejectedRides =
        (minRejectedRides === "" || rejectedRides >= minRejectedRides) &&
        (maxRejectedRides === "" || rejectedRides <= maxRejectedRides);

      const totalRides = driver.totalRideCount || 0;
      const matchesTotalRides =
        (minRides === "" || totalRides >= minRides) && (maxRides === "" || totalRides <= maxRides);

      return (
        matchesSearch &&
        matchesDriverType &&
        matchesCarType &&
        matchesRejectedRides &&
        matchesTotalRides
      );
    });
  };

  const renderFilters = () => (
    <Box>
      <TextField
        label="Search Drivers by name, email & phone number"
        value={search}
        onChange={handleSearchChange}
        fullWidth
        sx={{ mb: 2 }}
      />
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2, mb: 2 }}>
        <FormControl fullWidth>
          <InputLabel>Driver Type</InputLabel>
          <Select
            value={driverType}
            onChange={(e) => handleFilterChange(setDriverType)(e.target.value)}
            label="Driver Type"
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="normal">Normal</MenuItem>
            <MenuItem value="professional">Shared Drivers</MenuItem>
            <MenuItem value="delivery">Delivery</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel>Car Type</InputLabel>
          <Select
            value={carType}
            onChange={(e) => handleFilterChange(setCarType)(e.target.value)}
            label="Car Type"
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="NOCAR">No Car</MenuItem>
            <MenuItem value="CLASSIC">Classic</MenuItem>
            <MenuItem value="GREEN">Green or Comfort for IC</MenuItem>
            <MenuItem value="VAN">Van</MenuItem>
            <MenuItem value="MOTOR_BIKE">Motor Bike</MenuItem>
            <MenuItem value="TRIKE">Trike/Van...</MenuItem>
            <MenuItem value="MEDIUM_TRUCK">{"Medium Truck (< 1.5 ton)"}</MenuItem>
            <MenuItem value="LARGE_TRUCK">{"Large Truck (> 1.5 Ton)"}</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            label="Min Rejected Rides"
            value={minRejectedRides}
            onChange={(e) =>
              handleFilterChange(setMinRejectedRides)(e.target.value ? +e.target.value : "")
            }
            type="number"
            fullWidth
          />
          <TextField
            label="Max Rejected Rides"
            value={maxRejectedRides}
            onChange={(e) =>
              handleFilterChange(setMaxRejectedRides)(e.target.value ? +e.target.value : "")
            }
            type="number"
            fullWidth
          />
        </Box>

        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            label="Min Total Rides"
            value={minRides}
            onChange={(e) => handleFilterChange(setMinRides)(e.target.value ? +e.target.value : "")}
            type="number"
            fullWidth
          />
          <TextField
            label="Max Total Rides"
            value={maxRides}
            onChange={(e) => handleFilterChange(setMaxRides)(e.target.value ? +e.target.value : "")}
            type="number"
            fullWidth
          />
        </Box>
      </Box>
    </Box>
  );

  const renderDriverView = (drivers: User[], title: string) => {
    const displayTitle = title === "Offline Drivers" && viewMode === "map" 
      ? "Offline Drivers (Last Location)" 
      : title;
    
    const onlineDriverIds = new Set(connectedClients.map((client) => client.clientId));
    
    return (
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h6">{displayTitle}</Typography>
          <Box>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, newMode) => newMode && setViewMode(newMode)}
              sx={{ mr: 2 }}
            >
              <ToggleButton value="table">Table View</ToggleButton>
              <ToggleButton value="map">Map View</ToggleButton>
            </ToggleButtonGroup>
            <Button
              variant="outlined"
              onClick={() => setFiltersVisible((prev) => !prev)}
              sx={{ mr: 1 }}
            >
              {filtersVisible ? "Hide Filters" : "Show Filters"}
            </Button>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={() => loadAllDrivers()}
              sx={{ mr: 1 }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => downloadPDF(filterDrivers(drivers), title)}
            >
              Download PDF
            </Button>
          </Box>
        </Box>

        {filtersVisible && renderFilters()}

        {viewMode === "table" ? (
          <TableContainer component={Paper}>
            <Table>
              <TableHead style={{ backgroundColor: "rgb(224, 224, 224)" }}>
                <TableRow>
                  <TableCell>Driver ID</TableCell>
                  <TableCell>Driver Name</TableCell>
                  <TableCell>Car Type</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Driver Type</TableCell>
                  <TableCell>Internal/External</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Total Rides</TableCell>
                  <TableCell>Rejected Rides</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filterDrivers(drivers).map((driver) => {
                  const isOnline = onlineDriverIds.has(driver._id);
                  const isInternal = getDriverInternalStatus(driver._id);
                  return (
                    <TableRow key={driver._id}>
                      <TableCell>{driver._id}</TableCell>
                      <TableCell>{`${driver.firstName} ${driver.lastName}` || "-"}</TableCell>
                      <TableCell>{driver.carType || "-"}</TableCell>
                      <TableCell>{driver.email || "-"}</TableCell>
                      <TableCell>{driver.phone || "-"}</TableCell>
                      <TableCell>{driver.driverType ? getDriverTypes(driver) : "Normal"}</TableCell>
                      <TableCell>
                        <Chip
                          label={isInternal ? "Internal" : "External"}
                          color={isInternal ? "primary" : "info"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={isOnline ? "Online" : "Offline"}
                          color={isOnline ? "success" : "error"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{driver.totalRideCount || 0}</TableCell>
                      <TableCell>{driver.rejectedRideCount || 0}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <MapView
            drivers={filterDrivers(drivers)}
            connectedClients={connectedClients}
            selectedRegion={selectedRegion}
            partnerId={partnerId}
            partnerDriverIds={partnerDriverIds}
          />
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Partner Driver Map
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Online Drivers" />
          <Tab label="Offline Drivers" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography>Loading drivers...</Typography>
          </Box>
        ) : (
          renderDriverView(getOnlineDrivers(), "Online Drivers")
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography>Loading drivers...</Typography>
          </Box>
        ) : (
          renderDriverView(getOfflineDrivers(), "Offline Drivers")
        )}
      </TabPanel>
    </Box>
  );
};

export default PartnerMap;
